import { StatusTag } from "../../../../common/components/Status/StatusTag";
import { BookingDelayStatusConstant, BookingStateConstant } from "../core/_constant.booking";
import style from "../style.module.scss";
import { Image } from "antd";

interface BookingDetailsProps {
  value: any;
}

export const BookingDetails = ({ value }: BookingDetailsProps) => {
  const {
    reference = "-",
    blNumber = "-",
    createdAt = "-",
    badImportedAt = "-",
    latestGateOutDate = "-",
    finalCustomer = "-",
    bookingSource: { name: bookingSourceName = "-" } = {},
    bookingState: { id: bookingStateId = "-" } = {},
    customer: {
      name: customerName = "-",
      customerCategory: { name: customerCategoryName = "-" } = {},
    } = {},
    badDelayStatus: {
      deadline = "-",
      remainingDays = "-",
      status,
    } = {},
    deliveryNoteFile = null,
    containerIdentifiers = [],
    gateOutReferences = [],
    gateOuts = [],
  } = value;

  const delayStatusId = status?.id ?? "-";

  const getFileName = (file: any) => {
    if (!file) return "-";

    if (file.basename) {
      return decodeURIComponent(file.basename.split("?")[0]);
    }

    if (file.contentUrl) {
      const urlWithoutQuery = file.contentUrl.split("?")[0];
      return decodeURIComponent(
        urlWithoutQuery.substring(urlWithoutQuery.lastIndexOf("/") + 1)
      );
    }

    return "Document";
  };

  const formatFileSize = (size?: number) => {
    if (!size) return "-";

    if (size < 1024) {
      return `${size} octets`;
    }

    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} Ko`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className={style.details}>
      {/* Informations générales */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Informations générales
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>N° Booking</span>
            <strong>{reference}</strong>
          </div>

          <div className={style.details__item}>
            <span>N° BL</span>
            <strong>{blNumber}</strong>
          </div>

          <div className={style.details__item}>
            <span>Source de la réservation</span>
            <strong>{bookingSourceName}</strong>
          </div>

          <div className={style.details__item}>
            <span>Statut du Booking</span>
            <StatusTag
              value={bookingStateId}
              options={BookingStateConstant.OPTIONS}
            />
          </div>

          <div className={style.details__item}>
            <span>Date de création</span>
            <strong>{createdAt}</strong>
          </div>
        </div>
      </section>

      {/* Informations client */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Informations transitaire / client
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Nom du transitaire</span>
            <strong>{customerName}</strong>
          </div>

          <div className={style.details__item}>
            <span>Catégorie transitaire</span>
            <strong>{customerCategoryName}</strong>
          </div>

          <div className={style.details__item}>
            <span>Nom du client</span>
            <strong>{finalCustomer}</strong>
          </div>
        </div>
      </section>

      {/* Suivi des délais */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Suivi des délais & BAD
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Statut du délai</span>
            <StatusTag
              value={delayStatusId}
              options={BookingDelayStatusConstant.OPTIONS}
            />
          </div>

          <div className={style.details__item}>
            <span>Date limite</span>
            <strong>{deadline ?? "-"}</strong>
          </div>

          <div className={style.details__item}>
            <span>Jours restants</span>
            <strong>{remainingDays ?? "-"}</strong>
          </div>

          <div className={style.details__item}>
            <span>Date d'importation BAD</span>
            <strong>{badImportedAt}</strong>
          </div>

          <div className={style.details__item}>
            <span>Gate-out au plus tard</span>
            <strong>{latestGateOutDate}</strong>
          </div>
        </div>
      </section>

      {/* Bon de livraison */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Bon de livraison
        </h3>

        {deliveryNoteFile?.contentUrl ? (
          <div className={style.details__document}>
            <div className={style.details__document__info}>
              <span className={style.details__document__name}>
                {getFileName(deliveryNoteFile)}
              </span>

              <span className={style.details__document__meta}>
                {formatFileSize(deliveryNoteFile.size)}
              </span>
            </div>

            <a
              href={deliveryNoteFile.contentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={style.details__document__link}
            >
              Ouvrir le document
            </a>
          </div>
        ) : (
          <span className={style.details__empty}>
            Aucun bon de livraison disponible.
          </span>
        )}
      </section>

      {/* Conteneurs */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Conteneurs & références Gate Out
        </h3>

        {containerIdentifiers.length > 0 ? (
          <div className={style.details__containers}>
            {containerIdentifiers.map(
              (container: string, index: number) => (
                <div
                  key={`${container}-${index}`}
                  className={style.details__container}
                >
                  <span
                    className={
                      style.details__container__name
                    }
                  >
                    {container}
                  </span>

                  <span
                    className={
                      style.details__container__gateout
                    }
                  >
                    Gate Out :{" "}
                    {gateOutReferences[index] ?? "-"}
                  </span>
                </div>
              )
            )}
          </div>
        ) : (
          <span className={style.details__empty}>
            Aucun conteneur enregistré.
          </span>
        )}
      </section>

      {/* Contrôles Gate Out */}
      {/* <section className={style.details__section}>
        <h3 className={style.details__title}>
          Détails des contrôles Gate Out
        </h3>

        {gateOuts.length > 0 ? (
          <div className={style.details__gateOutList}>
            {gateOuts.map(
              (gateOut: any, gateOutIndex: number) => {
                const {
                  id,
                  createdAt: gateOutCreatedAt = "-",
                  checkedBy: {
                    fullname: checkedByFullname = "-",
                  } = {},
                  comment = "",
                  images = [],
                } = gateOut;

                return (
                  <div
                    key={id ?? gateOutIndex}
                    className={style.details__gateOutCard}
                  >
                    <div
                      className={
                        style.details__gateOutHeader
                      }
                    >
                      <strong>
                        Contrôle Gate Out
                        {gateOuts.length > 1
                          ? ` #${gateOutIndex + 1}`
                          : ""}
                      </strong>
                    </div>

                    <div className={style.details__grid}>
                      <div className={style.details__item}>
                        <span>Date du contrôle</span>
                        <strong>{gateOutCreatedAt}</strong>
                      </div>

                      <div className={style.details__item}>
                        <span>Contrôlé par</span>
                        <strong>{checkedByFullname}</strong>
                      </div>
                    </div>

                    {comment && (
                      <div
                        className={
                          style.details__gateOutBlock
                        }
                      >
                        <span
                          className={
                            style.details__gateOutLabel
                          }
                        >
                          Commentaire
                        </span>

                        <div className={style.details__comment}>
                          {comment}
                        </div>
                      </div>
                    )}

                    {images.length > 0 && (
                      <div
                        className={
                          style.details__gateOutBlock
                        }
                      >
                        <span
                          className={
                            style.details__gateOutLabel
                          }
                        >
                          Photos du contrôle
                        </span>

                        <div className={style.details__images}>
                          <Image.PreviewGroup>
                            {images.map(
                              (
                                image: any,
                                imageIndex: number
                              ) => (
                                <div
                                  key={
                                    image.id ??
                                    imageIndex
                                  }
                                  className={
                                    style.details__image
                                  }
                                >
                                  <Image
                                    src={
                                      image.contentUrl
                                    }
                                    alt={`Photo Gate Out ${
                                      imageIndex + 1
                                    }`}
                                  />
                                </div>
                              )
                            )}
                          </Image.PreviewGroup>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </div>
        ) : (
          <span className={style.details__empty}>
            Aucun contrôle Gate Out enregistré.
          </span>
        )}
      </section> */}
    </div>
  );
};